import { Handle, Position } from '@xyflow/react'
import { GitBranch, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { GraphNodeData } from '@/types/graph'
import { useCanvasStore } from '@store/canvas'

interface ConditionNodeProps {
  data: GraphNodeData
  selected?: boolean
  id: string
}

export default function ConditionNode({
  data,
  selected,
  id,
}: ConditionNodeProps) {
  const { t } = useTranslation()
  const removeNode = useCanvasStore((state) => state.removeNode)

  const expressionPreview = data.config?.logic
    ? String(data.config.logic).substring(0, 60)
    : t('build.noConditionSet')

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    removeNode(id)
  }

  return (
    <div
      className={`px-4 py-3 rounded-lg border-2 bg-card min-w-48 transition-all ${
        selected
          ? 'border-warning ring-2 ring-warning/30'
          : 'border-warning/30 hover:border-warning'
      }`}
    >
      <Handle type="target" position={Position.Top} />

      {/* Header */}
      <div className="flex items-center justify-between mb-2 group">
        <div className="flex items-center gap-2 flex-1">
          <GitBranch size={16} className="text-warning flex-shrink-0" />
          <div className="font-bold text-sm text-foreground truncate">
            {data.label || t('build.conditionalExpression')}
          </div>
        </div>
        <button
          onClick={handleDelete}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-error-soft rounded text-error"
          title={t('build.deleteNode')}
        >
          <X size={14} />
        </button>
      </div>

      {/* Body - rotated diamond visual hint */}
      <div className="text-xs text-foreground mb-3 bg-warning-soft rounded p-2 border-l-2 border-warning">
        <div className="font-mono text-foreground text-xs truncate">
          {expressionPreview}
        </div>
      </div>

      {/* Footer - Branch labels */}
      <div className="flex gap-2 text-xs font-semibold">
        <span className="px-2 py-1 bg-success-soft text-success rounded flex-1 text-center">
          {t('build.trueLabel')}
        </span>
        <span className="px-2 py-1 bg-error-soft text-error rounded flex-1 text-center">
          {t('build.falseLabel')}
        </span>
      </div>

      {/* True branch handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="true"
        style={{ left: '25%' }}
      />

      {/* False branch handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="false"
        style={{ left: '75%' }}
      />
    </div>
  )
}
